import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BookOpen, Plus, X, ExternalLink, FileText, Link as LinkIcon, Tag, Search, Trash2, Pencil, ShieldCheck, MessageSquareWarning } from "lucide-react";

const CT_LABEL = { text: "Text", file: "File", sharepoint: "SharePoint" };

function DocModal({ doc, onClose, onSaved }) {
  const { t } = useI18n();
  const isNew = !doc.id;
  const [form, setForm] = useState({
    title: doc.title || "",
    description: doc.description || "",
    category: doc.category || "Policies",
    content_type: doc.content_type || "text",
    content: doc.content || "",
    file_url: doc.file_url || "",
    file_name: doc.file_name || "",
    sharepoint_url: doc.sharepoint_url || "",
    sharepoint_site: doc.sharepoint_site || "",
    tags: (doc.tags || []).join(", "),
    active: doc.active !== false,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, file_url: r.data.url, file_name: file.name });
    } catch (err) { alert(t("common.upload_failed") + " " + (err?.response?.data?.detail || err.message)); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean) : [] };
      if (isNew) await api.post("/kb/documents", payload);
      else await api.patch(`/kb/documents/${doc.id}`, payload);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[760px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="sw-heading text-[20px]">{isNew ? "New" : "Edit"} document</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2"><label className="sw-label">Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sw-input" data-testid="kb-title" /></div>
          <div className="md:col-span-2"><label className="sw-label">Description *</label><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" data-testid="kb-description" /></div>
          <div><label className="sw-label">Category *</label>
            <input list="kb-categories" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="sw-input" />
            <datalist id="kb-categories"><option value="Policies" /><option value="HR" /><option value="Benefits" /><option value="Onboarding" /><option value="Performance" /><option value="Engineering" /><option value="Finance" /><option value="Compliance" /></datalist>
          </div>
          <div><label className="sw-label">Tags (comma-separated)</label><input placeholder="travel, expenses, ..." value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="sw-input" /></div>
          <div className="md:col-span-2">
            <label className="sw-label">Content source *</label>
            <div className="flex gap-2 mb-2">
              {["text", "file", "sharepoint"].map((ct) => (
                <button key={ct} type="button" onClick={() => setForm({ ...form, content_type: ct })} data-testid={`ct-${ct}`} className={`px-3 py-1.5 rounded-[10px] text-[12px] ${form.content_type === ct ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{CT_LABEL[ct]}</button>
              ))}
            </div>
            {form.content_type === "text" && <textarea rows={10} placeholder="Markdown content..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="sw-input font-mono !text-[12px]" data-testid="kb-content" />}
            {form.content_type === "file" && (
              <div className="p-4 rounded-[12px] bg-[#f6f6f6] border border-dashed border-[#475569]">
                <label className="sw-btn-secondary !text-[12px] cursor-pointer inline-flex items-center gap-1.5 w-fit">{uploading ? "..." : t("common.choose_file")}<input type="file" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="hidden" disabled={uploading} accept=".pdf,.docx,.doc,.txt,.md" data-testid="kb-file" /></label>
                {form.file_url && <div className="text-[12px] mt-2 text-[#008932]">✓ {form.file_name} · <a href={form.file_url} target="_blank" rel="noopener noreferrer" className="text-[#475569] underline">view</a></div>}
                <div className="text-[10px] text-[#747474] mt-2">{t("kn.upload_hint")} {t("kn.storage_hint")}</div>
                <textarea rows={4} placeholder={t("kn.paste_text_ph")} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="sw-input mt-2 !text-[12px]" />
              </div>
            )}
            {form.content_type === "sharepoint" && (
              <div className="p-4 rounded-[12px] bg-[#f6f6f6]">
                <input placeholder="SharePoint document URL (https://yourtenant.sharepoint.com/...)" value={form.sharepoint_url} onChange={(e) => setForm({ ...form, sharepoint_url: e.target.value })} className="sw-input mb-2" data-testid="kb-sp-url" />
                <input placeholder={t("kn.sharepoint_ph")} value={form.sharepoint_site} onChange={(e) => setForm({ ...form, sharepoint_site: e.target.value })} className="sw-input mb-2" />
                <textarea rows={5} placeholder={t("kn.paste_summary_ph")} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="sw-input !text-[12px]" />
                <div className="text-[10px] text-[#747474] mt-2">💡 Auto-sync via Microsoft Graph API is on the roadmap. For now, paste a summary or the most important content to make the doc searchable by Sage.</div>
              </div>
            )}
          </div>
          <label className="md:col-span-2 flex items-center gap-2 text-[13px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />{t("kn.active_visible")}</label>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="save-kb-btn" onClick={save} disabled={saving || !form.title || !form.description} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Knowledge() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showCopilotConfig, setShowCopilotConfig] = useState(false);
  const [gaps, setGaps] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState("docs");  // docs | gaps | stats

  const canManage = ["admin", "hr"].includes(user?.role);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterCat !== "all") params.set("category", filterCat);
    if (search) params.set("q", search);
    const [d, c] = await Promise.all([api.get(`/kb/documents?${params.toString()}`), api.get("/kb/categories")]);
    setDocs(d.data);
    setCategories(c.data);
  };
  useEffect(() => { load(); }, [filterCat, search]); // eslint-disable-line

  const loadStats = async () => {
    if (!canManage) return;
    const [g, s] = await Promise.all([api.get("/copilot/gaps"), api.get("/copilot/stats")]);
    setGaps(g.data);
    setStats(s.data);
  };
  useEffect(() => { loadStats(); }, []); // eslint-disable-line

  const removeDoc = async (d) => {
    if (!window.confirm(t("common.delete_named", { title: d.title }))) return;
    await api.delete(`/kb/documents/${d.id}`);
    load();
  };

  const resolveGap = async (g) => {
    await api.patch(`/copilot/gaps/${g.id}`, { status: "resolved" });
    loadStats();
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><BookOpen size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">Knowledge Base</h1>
            <p className="text-[14px] text-[#747474]">{t("kn.subtitle")}</p>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button data-testid="copilot-config-btn" onClick={() => setShowCopilotConfig(true)} className="sw-btn-secondary"><ShieldCheck size={14} /> LLM Config</button>
            <button data-testid="new-kb-btn" onClick={() => setEditing({})} className="sw-btn-primary"><Plus size={14} />  {t("kn.new_doc")}</button>
          </div>
        )}
      </div>

      {canManage && (
        <div className="flex gap-2 border-b border-[#eeeded] pb-2">
          <button onClick={() => setView("docs")} data-testid="view-docs" className={`px-3 py-1.5 rounded-[10px] text-[12px] ${view === "docs" ? "bg-[#e2e8f0] text-[#475569]" : "text-[#747474]"}`}>Documents ({docs.length})</button>
          <button onClick={() => setView("gaps")} data-testid="view-gaps" className={`px-3 py-1.5 rounded-[10px] text-[12px] ${view === "gaps" ? "bg-[#e2e8f0] text-[#475569]" : "text-[#747474]"}`}>
            Knowledge Gaps {stats && stats.open_gaps > 0 && <span className="ml-1 px-1.5 rounded-full bg-[#881a27] text-white text-[9px]">{stats.open_gaps}</span>}
          </button>
          <button onClick={() => setView("stats")} data-testid="view-stats" className={`px-3 py-1.5 rounded-[10px] text-[12px] ${view === "stats" ? "bg-[#e2e8f0] text-[#475569]" : "text-[#747474]"}`}>{t("kn.stats")}</button>
        </div>
      )}

      {view === "docs" && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[250px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#747474]" />
              <input placeholder={t("kn.search_ph")} value={search} onChange={(e) => setSearch(e.target.value)} className="sw-input pl-9" data-testid="kb-search" />
            </div>
            <button onClick={() => setFilterCat("all")} className={`px-3 py-1.5 rounded-[10px] text-[11px] ${filterCat === "all" ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{t("common.all")}</button>
            {categories.map((c) => (
              <button key={c.category} onClick={() => setFilterCat(c.category)} className={`px-3 py-1.5 rounded-[10px] text-[11px] ${filterCat === c.category ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{c.category} <span className="opacity-60">({c.count})</span></button>
            ))}
          </div>

          {docs.length === 0 ? (
            <div className="sw-card text-center py-12 text-[#747474]">{t("kn.no_docs")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {docs.map((d) => (
                <div key={d.id} className="sw-card !p-4 flex flex-col gap-2 hover:border-[#475569]">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#475569]">
                        {d.content_type === "text" ? <FileText size={10} /> : d.content_type === "file" ? <FileText size={10} /> : <LinkIcon size={10} />}
                        {d.category}
                      </div>
                      <div className="font-medium text-[14px] text-[#323232] mt-1 line-clamp-2">{d.title}</div>
                    </div>
                    {!d.active && <span className="sw-badge sw-badge-neutral !text-[9px]">inactive</span>}
                  </div>
                  <div className="text-[11px] text-[#747474] line-clamp-3">{d.description}</div>
                  {d.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {d.tags.slice(0, 4).map((t) => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-[5px] bg-[#f6f6f6] text-[#747474]"><Tag size={8} className="inline mr-0.5" />{t}</span>)}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-1 pt-2 border-t border-[#f6f6f6]">
                    <div className="text-[10px] text-[#747474]">
                      {d.sharepoint_url && <a href={d.sharepoint_url} target="_blank" rel="noopener noreferrer" className="text-[#475569] hover:underline"><ExternalLink size={9} className="inline" /> SharePoint</a>}
                      {d.file_url && <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-[#475569] hover:underline"><ExternalLink size={9} className="inline" /> File</a>}
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <button data-testid={`edit-kb-${d.id}`} onClick={() => setEditing(d)} className="text-[#747474] hover:text-[#475569]"><Pencil size={12} /></button>
                        <button onClick={() => removeDoc(d)} className="text-[#747474] hover:text-[#881a27]"><Trash2 size={12} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === "gaps" && canManage && (
        <div className="flex flex-col gap-2">
          {gaps.length === 0 ? <div className="sw-card text-center py-12 text-[#747474]">No knowledge gaps. 🎉</div> : gaps.map((g) => (
            <div key={g.id} className="sw-card !p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2"><MessageSquareWarning size={16} className="text-[#881a27]" /><span className="font-medium text-[14px]">{g.user_name}</span><span className="sw-badge sw-badge-pending !text-[10px]">{g.status}</span></div>
                {g.status === "open" && <button data-testid={`resolve-gap-${g.id}`} onClick={() => resolveGap(g)} className="sw-btn-secondary !text-[11px] !px-2 !py-0.5">Mark resolved</button>}
              </div>
              <div className="text-[12px] text-[#323232] mb-1"><span className="text-[#747474] font-semibold">Q:</span> {g.question}</div>
              <div className="text-[11px] text-[#747474] mb-1 line-clamp-2"><span className="font-semibold">A (bad):</span> {g.answer}</div>
              {g.comment && <div className="text-[11px] text-[#881a27] italic mt-1">"{g.comment}"</div>}
            </div>
          ))}
        </div>
      )}

      {view === "stats" && canManage && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t("kn.total_questions"), value: stats.total_questions },
            { label: t("kn.sessions"), value: stats.sessions },
            { label: t("kn.satisfaction"), value: `${stats.satisfaction_rate}%`, color: stats.satisfaction_rate >= 80 ? "#008932" : "#92400e" },
            { label: t("kn.verified_qa"), value: stats.verified_qa },
            { label: t("kn.thumbs_up"), value: stats.thumbs_up },
            { label: t("kn.thumbs_down"), value: stats.thumbs_down },
            { label: t("kn.open_gaps"), value: stats.open_gaps, color: stats.open_gaps > 0 ? "#881a27" : "#008932" },
          ].map((s, i) => (
            <div key={i} className="sw-card !p-4">
              <div className="text-[11px] uppercase tracking-wider text-[#475569]">{s.label}</div>
              <div className="text-[24px] font-bold mt-1" style={{ color: s.color || "#323232" }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {editing && <DocModal doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showCopilotConfig && <CopilotConfigModal onClose={() => setShowCopilotConfig(false)} />}
    </div>
  );
}

function CopilotConfigModal({ onClose }) {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ provider: "anthropic", model: "claude-sonnet-4-5-20250929", api_key: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/copilot/config").then((r) => { setCfg(r.data); if (r.data.custom_key_active) setForm({ provider: r.data.provider, model: r.data.model || "", api_key: "" }); });
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.post("/copilot/config", form); alert(t("kn.key_saved")); onClose(); }
    catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm(t("kn.key_reset_confirm"))) return;
    await api.delete("/copilot/config");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-xl">
        <div className="flex justify-between items-start mb-4">
          <h3 className="sw-heading text-[18px]">Sage LLM Configuration</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {cfg && (
          <div className="mb-4 p-3 rounded-[12px] bg-[#f6f6f6] text-[12px]">
            <div className="text-[#747474]">Current: <span className="font-semibold text-[#323232]">{cfg.custom_key_active ? "Custom key" : "Emergent Universal Key"}</span></div>
            <div className="text-[#747474] mt-1">Model: <span className="font-mono text-[11px]">{cfg.model || "claude-sonnet-4-5-20250929"}</span></div>
            {cfg.api_key_masked && <div className="text-[#747474] mt-1">API key: <span className="font-mono text-[11px]">{cfg.api_key_masked}</span></div>}
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div><label className="sw-label">Provider</label>
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="sw-input">
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
              <option value="gemini">Google (Gemini)</option>
            </select>
          </div>
          <div><label className="sw-label">Model</label>
            <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="claude-sonnet-4-5-20250929" className="sw-input font-mono !text-[12px]" />
          </div>
          <div><label className="sw-label">API Key</label>
            <input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="sk-..." className="sw-input font-mono !text-[12px]" data-testid="custom-api-key" />
            <div className="text-[10px] text-[#747474] mt-1">Stored encrypted in server. Leave blank to use Emergent key.</div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          {cfg?.custom_key_active && <button onClick={reset} className="sw-btn-secondary !text-[#881a27]">{t("kn.reset_emergent")}</button>}
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.api_key} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
